import { memo } from 'react'

import { Controller } from 'react-hook-form'

import CheckIcon from '@/assets/icons/icon-check.svg'
import ArrowIcon from '@/assets/icons/icon-chevron-down.svg'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.tsx'
import type { SelectOptionsType } from '@/components/ui/form/FormSelect'
import { classNames, unEscape } from '@/utils/helpers'

interface ListsSelectorProps {
  lists: SelectOptionsType
  name: string
  label: string
  disabled?: boolean
  backgroundCard?: boolean
  placeholder: string
  excludeValues?: string[]
}

export const ListsSelector = memo(
  ({
    lists,
    name,
    label,
    placeholder,
    disabled,
    backgroundCard,
    excludeValues = [],
  }: ListsSelectorProps) => {
    return (
      <Controller
        name={name}
        render={({ field, fieldState: { error } }) => {
          const selectedNames = lists
            .filter((option) => String(field.value)?.includes(option.value))
            .map((option) => option.label)

          return (
            <div className="flex flex-col gap-2">
              <label
                htmlFor={name}
                className="text-title text-sm font-medium"
              >
                {label}
              </label>
              <DropdownMenu>
                <DropdownMenuTrigger
                  disabled={disabled}
                  className={classNames(
                    'bg-background text-title focus:border-primary-500 focus:outline-primary-500 flex h-[40px] w-[240px] items-center justify-between rounded-md border  px-3 py-2',
                    {
                      'cursor-not-allowed opacity-50': disabled,
                      'bg-card': backgroundCard,
                    },
                  )}
                >
                  {selectedNames.length === 0 ? (
                    <span className="text-secondary-300 truncate text-sm">
                      {placeholder}
                    </span>
                  ) : (
                    <span className="truncate text-sm">
                      {selectedNames.join(', ')}
                    </span>
                  )}

                  <ArrowIcon className="[data-state=open]:rotate-180 fill-background" />
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="max-h-64 overflow-y-scroll p-2"
                  align="start"
                  forceMount
                >
                  <DropdownMenuGroup className="flex flex-col gap-[2px]">
                    {lists.length === 0 && (
                      <span className="text-text-sub text-sm">
                        No lists found
                      </span>
                    )}

                    {lists.map((option) => {
                      const included = [...field.value]
                        .map((value) =>
                          typeof value === 'number' ? String(value) : value,
                        )
                        .includes(option.value)

                      return (
                        <DropdownMenuItem
                          key={option.value}
                          disabled={excludeValues?.includes(option.value)}
                          className="text-title hover:bg-primary-500/20 hover:text-primary-500 group cursor-pointer gap-2 rounded-md"
                          onClick={(event) => {
                            event.stopPropagation()
                            event.preventDefault()

                            if (!included) {
                              field.onChange([
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                ...field.value.map((value: any) =>
                                  String(value),
                                ),
                                option.value,
                              ])

                              return
                            }

                            field.onChange(
                              field.value?.filter(
                                (id: string) =>
                                  id !== option.value &&
                                  String(id) !== option.value,
                              ),
                            )
                          }}
                        >
                          {included && (
                            <CheckIcon className="bg-primary-200/50 text-primary-500 rounded-sm border dark:text-white" />
                          )}
                          {unEscape(option.label)}
                        </DropdownMenuItem>
                      )
                    })}
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
              {error && (
                <p className="text-danger-default mt-2 text-xs">
                  {error.message}
                </p>
              )}
            </div>
          )
        }}
      />
    )
  },
)

ListsSelector.displayName = 'ListsSelector'
